// TournamentCard.jsx — improved UI (same props + behavior, better layout & visuals)
// Notes:
// - Keeps your existing props + click/keyboard behavior.
// - Adds optional logo/avatar placeholder (no new props needed).
// - Improves spacing, typography, badges, and button group.
// - Uses Bootstrap utility classes + your existing `.card` base.
//
// If you want an even cleaner look, paste the CSS snippet below this file too.

import {
  getEntityBadgeClasses,
  getEntityBadgeLabel,
  getStatusBadge,
  handleInteractiveKeyDown,
} from './cardUtils';
import { getTournamentOrganizerLabel } from '../../../utils/tournamentPresentation';

const TournamentCard = ({
  tournament,
  onClick,
  onEdit,
  onView,
  onManage,
  showActions = true,
  compact = false,
  showEntityBadge = true,
  isAdminView = false,
  entityType = null,
}) => {
  const formatDate = (value) => {
    if (!value) return 'TBD';
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? 'TBD' : parsedDate.toLocaleDateString();
  };

  const resolveEntityType = () => {
    if (entityType) return entityType;
    if (tournament?.entityType) return tournament.entityType;
    if (tournament?.organizationId || tournament?.organization?._id) return 'organization';
    if (tournament?.clubId || tournament?.club?._id) return 'club';
    if (tournament?.communityId || tournament?.community?._id) return 'community';
    return 'individual';
  };

  const normalizedEntityType = resolveEntityType();
  const startDate = formatDate(tournament?.startDate);
  const endDate = formatDate(tournament?.endDate);
  const status = getStatusBadge(
    tournament?.isActive === false ? 'completed' : tournament?.status,
    false
  );

  const interactive = typeof onClick === 'function';
  const title = tournament?.name || 'Untitled tournament';
  const organizerLabel = getTournamentOrganizerLabel(tournament);
  const registrationClosed = tournament?.registrationStatus === 'closed' || tournament?.registrationClosed;

  const handleOpen = (event) => {
    event?.stopPropagation?.();
    if (onManage) {
      onManage(tournament);
      return;
    }
    onClick?.(tournament);
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    if (onEdit) return onEdit(tournament);
    onClick?.(tournament);
  };

  const handleView = (event) => {
    event.stopPropagation();
    onView?.(tournament);
  };

  const entityLabel =
    isAdminView && normalizedEntityType === 'community'
      ? 'COMMUNITY'
      : isAdminView && normalizedEntityType === 'individual'
        ? 'INDIVIDUAL'
        : getEntityBadgeLabel(normalizedEntityType);

  // Nice small “avatar” circle with first letter (no new data needed)
  const avatarLetter = (title?.trim()?.[0] || 'T').toUpperCase();

  return (
    <article
      className={[
        'card common-entity-card',
        'shadow-sm border-0',
        interactive ? 'common-entity-card--interactive' : '',
        compact ? 'common-entity-card--compact' : '',
      ].join(' ')}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={() => onClick?.(tournament)}
      onKeyDown={(event) =>
        handleInteractiveKeyDown(event, () => onClick?.(tournament))
      }
      aria-label={`${title}${organizerLabel ? `, ${organizerLabel.toLowerCase()}` : ''}`}
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        cursor: interactive ? 'pointer' : 'default',
      }}
    >
      {/* subtle top accent */}
      <div
        style={{
          height: 4,
          background:
            'linear-gradient(90deg, rgba(31,162,255,0.9), rgba(79,195,247,0.9))',
        }}
      />

      <div className="card-body p-3">
        <div className="d-flex align-items-start gap-3">
          {/* avatar */}
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: compact ? 38 : 44,
              height: compact ? 38 : 44,
              borderRadius: 12,
              background: 'rgba(31, 162, 255, 0.10)',
              border: '1px solid rgba(31, 162, 255, 0.18)',
              fontWeight: 800,
              color: '#0b3a66',
            }}
            aria-hidden="true"
          >
            {avatarLetter}
          </div>

          <div className="flex-grow-1 min-w-0">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="min-w-0">
                <h3
                  className="mb-1"
                  style={{
                    fontSize: compact ? 15 : 16,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    letterSpacing: 0.1,
                  }}
                  title={title}
                >
                  <span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                    {title}
                  </span>
                </h3>
                {organizerLabel ? <p className="mb-1 text-muted text-truncate" style={{ fontSize: compact ? 12 : 13 }} title={organizerLabel}>{organizerLabel}</p> : null}

                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span
                    className="text-muted"
                    style={{ fontSize: compact ? 12 : 13 }}
                  >
                    {startDate} <span className="mx-1">→</span> {endDate}
                  </span>

                  {showEntityBadge ? (
                    <span
                      className={`badge ${getEntityBadgeClasses(normalizedEntityType)}`}
                      style={{
                        fontSize: 11,
                        padding: '6px 10px',
                        borderRadius: 999,
                        letterSpacing: 0.6,
                      }}
                    >
                      {entityLabel}
                    </span>
                  ) : null}
                  <span className={`badge ${registrationClosed ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`} style={{ fontSize: 11, padding: '6px 10px', borderRadius: 999 }}>
                    {registrationClosed ? 'Registration Closed' : 'Registration Open'}
                  </span>
                </div>
              </div>

              <span
                className={`badge ${status.className}`}
                style={{
                  borderRadius: 999,
                  padding: '6px 10px',
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                }}
              >
                {status.label}
              </span>
            </div>

            {/* optional: tiny secondary line (helpful for admin view) */}
            {isAdminView ? (
              <div className="mt-2 d-flex flex-wrap gap-2">
                <span
                  className="badge bg-light text-dark"
                  style={{ borderRadius: 999, padding: '6px 10px', fontSize: 11 }}
                >
                  Admin workspace
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {showActions ? (
          <div className="mt-3 d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleOpen}
                style={{ borderRadius: 10, fontWeight: 700 }}
              >
                {isAdminView ? 'Open workspace' : 'Open'}
              </button>

              {isAdminView ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleEdit}
                  style={{ borderRadius: 10, fontWeight: 700 }}
                >
                  Edit
                </button>
              ) : null}

              {!isAdminView && onView ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleView}
                  style={{ borderRadius: 10, fontWeight: 700 }}
                >
                  View
                </button>
              ) : null}
            </div>

            {/* right side hint */}
            {interactive ? (
              <span className="text-muted" style={{ fontSize: 12 }}>
                ↵ Enter
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default TournamentCard;
